/**
 * The form's side of the application-document rule (COL-618): PDF, JPG, PNG, WebP
 * or HEIC, at most 20 MB. The edge function create-application-account enforces
 * it again from the file's bytes, but it answers a refusal with an English
 * `error` the form does not show, so without this check an applicant who picked
 * the wrong file would only read "could not create your account".
 *
 * Judged by the declared type or the extension; the server is the strict half.
 * Held equal to supabase/functions/_shared/application-uploads.ts by
 * application-upload.parity.test.ts.
 */

/** 20 MB, matching the edge function and the admin Documents tab. */
export const MAX_APPLICATION_UPLOAD_BYTES = 20 * 1024 * 1024;

export const APPLICATION_UPLOAD_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

/** For the file input's `accept`: types and extensions, since some browsers send no type for .heic. */
export const APPLICATION_UPLOAD_ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif," +
  APPLICATION_UPLOAD_TYPES.join(",");

const EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "heif"];

export type ApplicationUploadProblem = "type" | "size";

export function applicationUploadProblem(file: {
  name: string;
  type: string;
  size: number;
}): ApplicationUploadProblem | null {
  const type = file.type.split(";")[0].trim().toLowerCase();
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  const known =
    (APPLICATION_UPLOAD_TYPES as readonly string[]).includes(type) ||
    (extension !== undefined && EXTENSIONS.includes(extension));
  if (!known || file.size <= 0) return "type";
  if (file.size > MAX_APPLICATION_UPLOAD_BYTES) return "size";
  return null;
}
